<template>
  <pp-container
    class="calendar-filters pb-0 pt-0 white"
    :class="[
      isMobile ? 'px-3' : 'px-8 mt-0',
      showFilterRow ? '' : 'calendar-filters--empty',
    ]"
  >
    <component
      :is="isMobile? 'collapse-item' : 'pp-row'"
      v-if="showFilterRow"
      v-model="filterRowOpen"
      label="Filters"
      class="d-flex align-center filters-container row"
      :class="filterRowClasses"
      cols="12"
    >
      <filter-item
        v-for="filter in filtersItems"
        :key="filter.name"
        :filter-info="filter"
        :hide-details="hideDetails"
        :show-filter-label="showFilterLabel"
        :desktop-cols-count="getDesktopColCountForFilter(filter.name)"
        :class="[
          ...getColClasses(filter.name)
        ]"
        @filterChanged="$emit('filterChanged', $event)"
        @trackFilterByName="$emit('trackFilterByName', $event)"
      />
      <pp-button
        v-if="requireSearchBeforeTimeShowing"
        class="mb-auto search-btn"
        :class="[
          isMobile ? 'mx-auto col-12 text-h3' : 'ml-3 mt-7'
        ]"
        type="primary"
        :icon="isMobile? '' :'search'"
        :text="isMobile ? 'Search' : ''"
        :prepend-icon="isMobile ? 'search text-h3 align-center' :''"
        @click="searchCalendars"
      />
    </component>
    <pp-divider
      v-if="!isMobile"
      class="mx-n8"
      :class="[
        isMobile ? 'my-0' : (showFilterRow ? 'mb-1 mt-4' : 'mt-7')
      ]"
    />
  </pp-container>
</template>

<script>
import CollapseItem from '@/components/collapse-item/CollapseItem'
import FilterItem from '@/components/filter-item/FilterItem'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'FiltersRow',
  components: {
    'collapse-item': CollapseItem,
    'filter-item': FilterItem
  },
  props: {
    showFilterRow: {
      default: false,
      type: Boolean
    },
    requireSearchBeforeTimeShowing: {
      default: false,
      type: Boolean
    },
    filtersInfo: {
      required: true,
      type: Array
    },
    allShowedFiltersFilled: {
      default: false,
      type: Boolean
    }
  },
  data: function () {
    return {
      filterRowOpen: true,
      isMobile: this.$ppUi.isMobile
    }
  },
  computed: {
    ...mapState('legacy', ['appointmentTypesEnabled']),
    ...mapGetters({
      filters: 'ui/filters'
    }),
    filterRowClasses () {
      const list = []
      list.push(this.isMobile ? 'mt-0 pt-0 px-3 pb-3' : 'pt-0')
      list.push(!this.isMobile && this.requireSearchBeforeTimeShowing ? 'mt-4' : '')
      list.push(!this.isMobile && !this.requireSearchBeforeTimeShowing ? 'mt-n2' : '')
      if (this.requireSearchBeforeTimeShowing) {
        list.push('filters-container--with-search')
      }
      return list
    },
    getColClasses () {
      return (filterName) => {
        const list = []
        if (this.isMobile) {
          list.push('px-0')
        } else if (this.requireSearchBeforeTimeShowing) {
          list.push('mb-0')
        } else {
          list.push('mx-0')
        }
        switch (filterName) {
          case 'newPatientFilter':
            list.push('filters-container__item--new-patient-filter')
            if (this.isMobile) {
              list.push('d-flex')
            }
            break
        }
        return list
      }
    },
    showFilterLabel () {
      return !this.isMobile
    },
    hideDetails () {
      return this.requireSearchBeforeTimeShowing ? 'auto' : 'hide'
    },
    filterSortOrderMap () {
      const sortOrder = {
        newPatientFilter: 0,
        visitTypeFilter: 100,
        appointmentReasonFilter: 200,
        locationIdFilter: 300,
        providerIdFilter: 400,
        insuranceProviderIdFilter: 500
      }
      if (!this.appointmentTypesEnabled) {
        sortOrder.appointmentReasonFilter = sortOrder.providerIdFilter + 1
      }
      return sortOrder
    },
    filtersItems () {
      return this.filtersInfo.map(item => ({
        ...item,
        sortOrder: this.filterSortOrderMap[item.name]
      })).sort((itemOne, itemTwo) => itemOne.sortOrder - itemTwo.sortOrder)
    },
    showedDropdownFiltersCont () {
      return this.filtersInfo.reduce((acc, item) => {
        if (item.filterComponent === 'pp-input-select' && item.showFilter) {
          acc++
        }
        return acc
      }, 0)
    }
  },
  methods: {
    searchCalendars () {
      this.allShowedFiltersFilled && (this.filterRowOpen = false)
      this.$emit('searchCalendars')
    },
    getDesktopColCountForFilter (filterName) {
      if (filterName === 'newPatientFilter') {
        return 12
      }
      // by default 4 columns pre filter
      // if search required or 4+ filters showed as dropdown 3 columns per filter
      return this.showedDropdownFiltersCont > 3 || this.requireSearchBeforeTimeShowing ? 3 : 4
    }
  }
}
</script>

<style scoped lang="scss">
.filters {
  z-index: 855;
}

.calendar-filters {
  &:not(&--empty) {
    min-height: 50px;
  }
}

.filters-container {
  $filtersContainer: &;

  flex-wrap: wrap;
  max-width: none;
  min-width: 100%;
  width: auto;

  &--with-search {
    #{$filtersContainer}__item {
      &:nth-child(3n + 1):not(:last-of-type) {
        margin-right: 25%;
        /* stylelint-disable max-nesting-depth */
        @include mobile() {
          margin-right: 0;
        }
        /* stylelint-enable max-nesting-depth */
      }
    }
  }

  .search-btn {
    align-self: flex-start;

    @include mobile() {
      margin-top: 0;
    }
  }

  &__item {
    align-self: flex-start;
    box-sizing: border-box;
    min-height: 48px;

    &--new-patient-filter {
      align-self: center;
    }

    ::v-deep.v-input__slot { /* stylelint-disable-line selector-class-pattern */
      min-height: 36px;
    }

    @include mobile() {
      padding-bottom: 16px;

      &:last-child {
        padding-bottom: 19px;
      }
    }

    @include desktop() {
      &.mr-2:last-child {
        /* stylelint-disable declaration-no-important */
        margin-right: 0 !important;
        /* stylelint-enable declaration-no-important */
      }

      .pp-toggle-block {
        height: 40px;
      }
    }

    &__label {
      line-height: 1em;

      &--disabled {
        opacity: .5;
      }
    }
  }
}
</style>
